import { FormInstance, Input, Select, DatePicker, InputNumber, TimePicker, Space, UploadProps, message, Upload } from 'antd';
import { Field } from '../models';
import { useEffect, useRef, useState } from 'react';
import { apiClient, apiErrorMessage, getApiBaseUrl } from '../utils';
import moment from 'moment';
import { LocalImage } from './local-image';
import { DebounceSelect } from './debounce-select';
import { TextEditor } from '.';
import { InboxOutlined } from '@ant-design/icons';

export function CellValueInput(props: {
  field?: Field;
  value?: any;
  recordId?: string;
  form?: FormInstance<any>;
  forFilter?: boolean;
  style?: React.CSSProperties;
  placeholder?: string;
  userHasWorkflowPermission?: boolean;
  onChange?: (value: any) => void;
}) {
  const [fkOptions, setFkOptions] = useState<{ value: string; label: string }[]>();
  const [fkOptionsLoading, setFkOptionsLoading] = useState(false);
  const [fkOptionsLoaded, setFkOptionsLoaded] = useState(false);
  const [fkOptionsExceedsLimit, setFkOptionsExceedsLimit] = useState(false);

  const baseApiUrl = getApiBaseUrl() + '/api/';
  const defaultStyle = { width: '300px' };

  const numericValueRef = useRef<number>();
  const uomValueRef = useRef<string>();

  const { field, value, forFilter, style, userHasWorkflowPermission, onChange } = props;
  const fieldDataType = field?.dataType;
  const isRichTextEditor = field?.isRichTextEditor;
  const picklistValues = field?.picklistValues;
  const unitsOfMeasure = field?.unitsOfMeasure;
  const isWorkflow = field?.isWorkFlow;

  useEffect(() => {
    if (!field || (fieldDataType !== 'FOREIGNKEY' && fieldDataType !== 'MULTIFOREIGNKEY')) return;

    const queryParams = [`gridId=${field?.foreignKeyGridId}`, `summaryFieldId=${field?.foreignKeyFieldId}`];

    setFkOptionsLoading(true);
    apiClient
      .get(`records/rows/foreign-keys?${queryParams.join('&')}`)
      .then(response => {
        const responseFkOptions = response.data.options as { value: string; label: string }[];
        const responseFkOptionsCount = response.data.totalCount as number;
        setFkOptions(responseFkOptions);

        if (responseFkOptionsCount > 1000) {
          setFkOptionsExceedsLimit(true);
        }

        setFkOptionsLoaded(true);
      })
      .catch(reason => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setFkOptionsLoading(false);
      });
  }, [fieldDataType, field]);

  const searchFkOptions = async (search: string) => {
    if (fkOptionsLoaded && search?.trim().length < 4) {
      return [];
    }

    setFkOptionsLoading(true);

    const gridId = field?.foreignKeyGridId;
    const queryParams = [`gridId=${gridId}`, `summaryFieldId=${field?.foreignKeyFieldId}`, `search=${search}`];

    try {
      const response = await apiClient.get(`records/rows/foreign-keys?${queryParams.join('&')}`);
      const responseFkOptions = response.data.options as { value: string; label: string }[];
      setFkOptions(responseFkOptions || []);
      setFkOptionsLoaded(true);
      setFkOptionsLoading(false);
      return responseFkOptions;
    } catch (reason: any) {
      setFkOptionsLoading(false);
      apiErrorMessage(reason);
      return [];
    }
  };

  const uploadFile = async (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;

    const fmData = new FormData();
    fmData.append('file', file);
    fmData.append('directory', props?.field?.id ?? '');
    try {
      await apiClient.post('files/upload', fmData, {
        onUploadProgress: ({ total, loaded }) => {
          onProgress({ percent: Math.round((loaded / total) * 100).toFixed(2) }, file);
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onSuccess('Ok');
    } catch (err) {
      console.log('Error: ', err);
      onError({ err });
    }
  };

  const onNumericChange = (value: any) => {
    numericValueRef.current = Number(value);
    if (uomValueRef.current) {
      onChange?.(`${value} ${uomValueRef.current}`);
    } else {
      onChange?.(value);
    }
  };

  const onUomChange = (value: any) => {
    uomValueRef.current = String(value);
    if (numericValueRef.current) {
      onChange?.(`${numericValueRef.current} ${value}`);
    } else {
      onChange?.(value);
    }
  };

  const renderUnitOfMeasureSelect = () => {
    if (!unitsOfMeasure) return null;
    return (
      <Select
        value={value}
        style={{ ...defaultStyle, ...style }}
        options={unitsOfMeasure?.map(unit => ({ value: unit, label: unit }))}
        onChange={onUomChange}
      />
    );
  };

  const renderStringInput = () => {
    return <Input value={value} style={{ ...defaultStyle, ...style }} placeholder={props.placeholder} onChange={e => onChange?.(e.target.value)} />;
  };

  const renderTextInput = () => {
    return <Input.TextArea value={value} style={{ ...defaultStyle, ...style }} placeholder={props.placeholder} onChange={e => onChange?.(e.target.value)} />;
  };

  const renderRichTextInput = () => {
    return <TextEditor value={value} onChange={value => onChange?.(value)} />;
  };

  const renderIntegerInput = () => {
    return (
      <Space>
        <InputNumber value={value} style={{ ...defaultStyle, ...style }} onChange={onNumericChange} precision={0} />
        {renderUnitOfMeasureSelect()}
      </Space>
    );
  };

  const renderDoubleInput = () => {
    return (
      <Space>
        <InputNumber value={value} style={{ ...defaultStyle, ...style }} onChange={onNumericChange} precision={2} />
        {renderUnitOfMeasureSelect()}
      </Space>
    );
  };

  const renderDecimalInput = () => {
    return (
      <Space>
        <InputNumber value={value} style={{ ...defaultStyle, ...style }} onChange={onNumericChange} precision={4} />
        {renderUnitOfMeasureSelect()}
      </Space>
    );
  };

  const renderPicklistInput = () => {
    const imageStyle = {
      width: field?.picklistImageWidth + 'px',
      marginRight: (field?.picklistImageWidth || 0) > 0 ? '0.75rem' : '0',
    };

    const currentStep = isWorkflow ? picklistValues?.find(pv => pv.id === props?.value) : undefined;
    const currentStepIndex = currentStep ? picklistValues?.indexOf(currentStep) : 0;

    return (
      <Select
        value={value}
        showSearch
        filterOption={(input, option) => {
            if (!option?.label || !input) {
              return false;
            }
            return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
        style={{ ...defaultStyle, ...style }}
        onChange={value => onChange?.(value)}
      >
        {picklistValues?.map((pv, index) => (
          <Select.Option
            key={pv.id}
            value={pv.id}
            disabled={!userHasWorkflowPermission && !forFilter && isWorkflow && index < (currentStepIndex || 0)}
          >
            <div>
              {pv.image && <LocalImage src={`${baseApiUrl}fields/picklist-image?name=${pv.image}`} alt={pv.val} style={imageStyle} />}
              {!pv.image && picklistValues?.some(v => !!v.image) && (field?.picklistImageWidth || 0) > 0 && (
                <div style={{ display: 'inline-block', ...imageStyle }}></div>
              )}
              <span className="option-text">{pv.val}</span>
            </div>
          </Select.Option>
        ))}
      </Select>
    );
  };

  const renderMultiPicklistInput = () => {
    const imageStyle = {
      width: field?.picklistImageWidth + 'px',
      marginRight: (field?.picklistImageWidth || 0) > 0 ? '0.75rem' : '0',
    };

    return (
      <Select
        value={value}
        showSearch
        filterOption={(input, option) => {
          if (!option?.label || !input) {
            return false;
          }
          return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }}
        style={{ ...defaultStyle, ...style }}
        onChange={value => {
          onChange?.(value);
        }}
        mode="multiple"
      >
        {picklistValues?.map(pv => (
          <Select.Option key={pv.id} value={pv.id}>
            <div>
              {pv.image && <LocalImage src={`${baseApiUrl}fields/picklist-image?name=${pv.image}`} alt={pv.val} style={imageStyle} />}
              {!pv.image && picklistValues?.some(v => !!v.image) && (field?.picklistImageWidth || 0) > 0 && (
                <div style={{ display: 'inline-block', ...imageStyle }}></div>
              )}
              <span className="option-text">{pv.val}</span>
            </div>
          </Select.Option>
        ))}
      </Select>
    );
  };

  const renderForeignKeyInput = () => {
    if (!forFilter && field?.mainForeignKeyFieldId) {
      return null;
    }

    if (!fkOptionsExceedsLimit) {
      return (
        <Select
          value={value}
          loading={fkOptionsLoading}
          showSearch
          filterOption={(input, option) => {
            if (!option?.label || !input) {
              return false;
            }
            return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          allowClear
          onChange={value => onChange?.(value)}
          options={fkOptions}
          style={{ ...defaultStyle, ...style }}
        />
      );
    }

    return <DebounceSelect loading={fkOptionsLoading} allowClear fetchOptions={searchFkOptions} />;
  };

  const renderMultiForeignKeyInput = () => {
    if (!forFilter && field?.mainForeignKeyFieldId) {
      return null;
    }

    if (!fkOptionsExceedsLimit) {
      return (
        <Select
          value={value}
          loading={fkOptionsLoading}
          showSearch
          filterOption={(input, option) => {
            if (!option?.label || !input) {
              return false;
            }
            return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          allowClear
          onChange={value => onChange?.(value)}
          options={fkOptions}
          mode="multiple"
          style={{ ...defaultStyle, ...style }}
        />
      );
    }

    return (
      <DebounceSelect
        loading={fkOptionsLoading}
        allowClear
        fetchOptions={searchFkOptions}
        mode="multiple"
        style={{ ...defaultStyle, ...style }}
      />
    );
  };

  const renderBooleanInput = () => {
    return (
      <Select value={value} style={{ ...defaultStyle, ...style }} onChange={value => onChange?.(value)} allowClear>
        <Select.Option value="true">Evet</Select.Option>
        <Select.Option value="false">Hayır</Select.Option>
      </Select>
    );
  };

  const renderDateInput = () => {
    return (
      <DatePicker
        value={value ? moment(value) : undefined}
        style={{ ...defaultStyle, ...style }}
        onChange={(_, dateString) => onChange?.(dateString)}
      />
    );
  };

  const renderDateTimeInput = () => {
    return (
      <DatePicker
        value={value ? moment(value) : undefined}
        style={{ ...defaultStyle, ...style }}
        onChange={(_, dateString) => onChange?.(dateString)}
        showTime
      />
    );
  };

  const renderTimeInput = () => {
    return (
      <TimePicker
        value={value ? moment(value, 'HH:mm:ss') : undefined}
        style={{ ...defaultStyle, ...style }}
        onChange={(_, timeString) => onChange?.(timeString)}
      />
    );
  };

  const renderUrlInput = () => {
    return <Input value={value} style={{ ...defaultStyle, ...style }} onChange={e => onChange?.(e.target.value)} type="url" />;
  };

  const renderLookupInput = () => {
    if (!forFilter) return null;
    return <Input value={value} style={{ ...defaultStyle, ...style }} onChange={e => onChange?.(e.target.value)} />;
  };

  const renderDynamicInput = () => {
    if (!forFilter) return null;
    return <Input value={value} style={{ ...defaultStyle, ...style }} onChange={e => onChange?.(e.target.value)} />;
  };

  const renderRuleBasedInput = () => {
    if (!forFilter) return null;
    return <Input value={value} style={{ ...defaultStyle, ...style }} onChange={e => onChange?.(e.target.value)} />;
  };

  const renderParentRowInput = () => {
    return (
      <Select
        loading={fkOptionsLoading}
        showSearch
        filterOption={(input, option) => {
          if (!option?.label || !input) {
            return false;
          }
          return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }}
        style={{ ...defaultStyle, ...style }}
        onChange={value => onChange?.(value)}
      >
        {fkOptions?.map(o => (
          <Select.Option key={o.value} value={o.value}>
            {o.label}
          </Select.Option>
        ))}
      </Select>
    );
  };

  const renderAutoIncInput = () => {
    if (!forFilter) return null;
    return <InputNumber value={value} style={{ ...defaultStyle, ...style }} onChange={onNumericChange} precision={0} />;
  };

  const renderSubGridInput = () => {
    if (forFilter) {
      return <Input value={value} style={{ ...defaultStyle, ...style }} onChange={e => onChange?.(e.target.value)} />;
    }

    return null;
  };

  const renderFileUploadInput = () => {
    const uploadProps: UploadProps = {
      name: 'file',
      multiple: true,
      action: getApiBaseUrl() + '/api/files/upload',
      customRequest: uploadFile,
      accept: field?.allowedFileExtensions?.join(',') ?? '*',
      beforeUpload: file => {
        const isSizeValid = file.size / 1024 / 1024 < (field?.maxFileSize ?? 10);
        if (!isSizeValid) {
          message.error(`Dosya boyutu ${field?.maxFileSize ?? 10}MB'dan büyük olamaz`);
        }
        return isSizeValid;
      },
      onChange(info: any) {
        if (info.fileList.length > (field?.maxFileCount ?? 1)) {
          message.error(`En fazla ${field?.maxFileCount ?? 1} dosya yüklenebilir`);
          return;
        }
      },
    };

    if (forFilter) {
      return <Input value={value} style={{ ...defaultStyle, ...style }} onChange={e => onChange?.(e.target.value)} />;
    }

    return (
      <Upload.Dragger {...uploadProps} style={{ ...defaultStyle, ...style }}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Buraya tıklayarak dosya yükleme işleminizi başlatabilirsiniz</p>
      </Upload.Dragger>
    );
  };

  switch (fieldDataType) {
    case 'STRING':
      if (isRichTextEditor) {
        return renderRichTextInput();
      }
      return renderStringInput();
    case 'TEXT':
      if (isRichTextEditor) {
        return renderRichTextInput();
      }
      return renderTextInput();
    case 'INTEGER':
      return renderIntegerInput();
    case 'DOUBLE':
      return renderDoubleInput();
    case 'DECIMAL':
      return renderDecimalInput();
    case 'PICKLIST':
      return renderPicklistInput();
    case 'MULTIPICKLIST':
      return renderMultiPicklistInput();
    case 'FOREIGNKEY':
      return renderForeignKeyInput();
    case 'MULTIFOREIGNKEY':
      return renderMultiForeignKeyInput();
    case 'BOOLEAN':
      return renderBooleanInput();
    case 'DATE':
      return renderDateInput();
    case 'DATETIME':
      return renderDateTimeInput();
    case 'TIME':
      return renderTimeInput();
    case 'URL':
      return renderUrlInput();
    case 'LOOKUP':
      return renderLookupInput();
    case 'DYNAMIC':
      return renderDynamicInput();
    case 'RULEBASED':
      return renderRuleBasedInput();
    case 'PARENTROW':
      return renderParentRowInput();
    case 'AUTOINC':
      return renderAutoIncInput();
    case 'SUBGRID':
      return renderSubGridInput();
    case 'FILE_UPLOAD':
      return renderFileUploadInput();
    default:
      return renderStringInput();
  }
}
